import React, { useState } from 'react';
import { Button } from 'antd';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

interface ArchiveRowActionProps {
	entity: any;
	onArchive: (entity: any) => Promise<any> | void;
	onUnarchive: (entity: any) => Promise<any> | void;
	// Disabled while a sibling row action is running.
	disabled?: boolean;
	// Notifies the parent row when this action starts/finishes so it can disable
	// its other buttons for the duration of the call.
	onLoadingChange?: (loading: boolean) => void;
	// Set when onArchive/onUnarchive defer the write behind an Undo toast (RestCrudThunks
	// `archive(..., showUndo=true)`). That thunk's promise never settles if the user clicks
	// Undo, so we must NOT tie a spinner to it — the toast is the feedback and the row would
	// otherwise hang in a loading state forever after an undone archive.
	deferred?: boolean;
}

/**
 * Per-row Archive (for active records) or Unarchive (for archived records) button.
 * Keyed off the record's `isArchived` flag. For immediate writes the button shows a
 * loading spinner while in flight and `onLoadingChange` lets the parent disable sibling
 * buttons; for `deferred` (undo-toast) writes no spinner is shown. Stops click propagation
 * so the surrounding clickable table row does not navigate.
 */
export const ArchiveRowAction: React.FC<ArchiveRowActionProps> = ({
	entity,
	onArchive,
	onUnarchive,
	disabled,
	onLoadingChange,
	deferred,
}) => {
	const [loading, setLoading] = useState(false);
	const isArchived = nullSafeGetOrElse('isArchived', entity, false);

	const handleClick = (e) => {
		e.stopPropagation();
		const runAction = isArchived ? onUnarchive : onArchive;
		if (deferred) {
			// Fire and forget: the Undo toast handles feedback and the returned promise may
			// never settle, so holding a spinner here would strand the row in a loading state.
			runAction(entity);
			return;
		}
		setLoading(true);
		onLoadingChange && onLoadingChange(true);
		Promise.resolve(runAction(entity)).finally(() => {
			setLoading(false);
			onLoadingChange && onLoadingChange(false);
		});
	};

	return (
		<Button loading={loading} disabled={disabled} onClick={handleClick}>
			{isArchived ? 'Unarchive' : 'Archive'}
		</Button>
	);
};

export default ArchiveRowAction;
